/**
 * The states a screen can be in besides "here is the data".
 *
 * Kept together and shared rather than written per screen. Each of these is a
 * moment when the owner is unsure what is happening, and an inconsistent
 * answer — a spinner here, a blank there, a raw error somewhere else — is what
 * makes a system feel untrustworthy exactly when trust matters.
 *
 * None of them invents a number. An empty screen says it is empty; it never
 * shows a zero, because a zero is a measurement and "we do not know" is not.
 */
import {
  AlertTriangle,
  Ban,
  Clock,
  Inbox,
  Loader2,
  PlugZap,
  Unplug,
  type LucideIcon,
} from "lucide-react";

type StateProps = {
  readonly title: string;
  // Explicitly `| undefined` rather than only optional: with
  // `exactOptionalPropertyTypes` those are different, and passing a value that
  // may be undefined is exactly what these do.
  readonly detail?: string | undefined;
  readonly action?: React.ReactNode | undefined;
};

type FrameProps = StateProps & {
  readonly icon: LucideIcon;
  readonly tone: "neutral" | "warning" | "danger";
  readonly role?: "status" | "alert";
  readonly spin?: boolean;
};

const TONE: Record<FrameProps["tone"], string> = {
  neutral: "text-text-secondary",
  warning: "text-status-warning",
  danger: "text-status-negative",
};

function Frame({
  icon: Icon,
  tone,
  title,
  detail,
  action,
  role = "status",
  spin = false,
}: FrameProps): React.JSX.Element {
  return (
    <div
      role={role}
      // Announced to a screen reader without stealing focus. A state that
      // changed silently would leave somebody waiting for a page that had
      // already answered.
      aria-live={role === "alert" ? "assertive" : "polite"}
      // A row, not a poster. Centred over 40px of vertical padding this state
      // took 250px to say one sentence, and on Стратегия it pushed the table
      // it was apologising for below the fold — a message about missing data
      // that hides the data which did arrive. Two radii exist and `lg` is not
      // one of them; the panel radius is the one this shares with every other
      // block on the page.
      className="flex items-start gap-3 rounded-panel border border-border-subtle bg-surface-default p-3"
    >
      <Icon
        aria-hidden="true"
        className={`mt-0.5 size-4 shrink-0 ${TONE[tone]} ${spin ? "animate-spin motion-reduce:animate-none" : ""}`}
      />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-body-main font-medium text-text-primary">{title}</p>
        {detail !== undefined && (
          <p className="max-w-prose text-helper-sm text-text-secondary">{detail}</p>
        )}
        {action}
      </div>
    </div>
  );
}

/** Something is being fetched. Says what, so the wait has a subject. */
export function Loading({ title = "Загружаю…", detail }: Partial<StateProps>): React.JSX.Element {
  return <Frame icon={Loader2} tone="neutral" title={title} detail={detail} spin />;
}

/** There is nothing to show, and that is the truth rather than a failure. */
export function Empty({ title, detail, action }: StateProps): React.JSX.Element {
  return <Frame icon={Inbox} tone="neutral" title={title} detail={detail} action={action} />;
}

/** Something went wrong. Never shows the raw message from the server. */
export function ErrorState({ title, detail, action }: StateProps): React.JSX.Element {
  return (
    <Frame
      icon={AlertTriangle}
      tone="danger"
      role="alert"
      title={title}
      detail={detail}
      action={action}
    />
  );
}

/** The figures are real but old. Shown beside them, never instead of them. */
export function Stale({ title = "Данные устарели", detail }: Partial<StateProps>): React.JSX.Element {
  return <Frame icon={Clock} tone="warning" title={title} detail={detail} />;
}

/** The exchange is not answering. The account is unchanged; the view is not. */
export function Disconnected({
  title = "Биржа не отвечает",
  detail = "Позиции и ордера не менялись — их просто не видно отсюда.",
}: Partial<StateProps>): React.JSX.Element {
  return <Frame icon={PlugZap} tone="warning" role="alert" title={title} detail={detail} />;
}

/**
 * The account has never been read on its exchange: nothing to show yet.
 *
 * Neither an outage nor an empty result. Calling it "could not read" sent the
 * owner looking for a failure, and a page of zeros would say the account lost
 * everything; it simply has not been connected.
 */
export function NotObserved({
  title = "Счёт ещё не подключён к бирже",
  detail = "Капитал, циклы и результат появятся после подключения Bybit и первого снимка счёта. Торговля сейчас выключена.",
}: Partial<StateProps>): React.JSX.Element {
  return <Frame icon={Unplug} tone="neutral" title={title} detail={detail} />;
}

/** This part does not exist in this version. Said plainly, not hidden. */
export function Forbidden({
  title = "Раздела пока нет",
  detail = "Он появится в следующей версии. Ссылка оставлена, чтобы было видно, что именно впереди.",
}: Partial<StateProps>): React.JSX.Element {
  return <Frame icon={Ban} tone="neutral" title={title} detail={detail} />;
}
